// HeroUploadModal — owner-facing hero photo flow.
//
// Two stages, one modal:
//   Stage 1 ("edit"): crop the source image to 1032×336 (the wallet hero
//     aspect) via react-easy-crop, then darken with a brightness slider
//     (range HERO_BRIGHTNESS_MIN..HERO_BRIGHTNESS_MAX, default 0). Live
//     preview uses CSS filter so the owner sees what they're going to get
//     before paying for an upload round-trip; the final brightness lives in
//     the bytes sharp produces server-side, so the on-screen approximation
//     and the rendered hero may differ by a few units of luminance — close
//     enough for V1.
//   Stage 2 ("confirm"): friction modal showing the cropped + brightened
//     preview, copy explaining that existing customer passes will update on
//     next stamp. Clicking "Save design" calls onConfirm with the cropped
//     PNG Blob + brightness number — the caller POSTs to /hero.
//
// Re-edit flow: when the program already has a hero, the parent passes
// `initialOriginalUrl` (the R2-served cropped PNG, no brightness applied)
// and `initialBrightness`. The modal opens with that URL pre-loaded for
// brightness re-editing. Re-cropping requires re-uploading from scratch —
// the cropped-is-original rule.

import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import {
  HERO_BRIGHTNESS_MAX,
  HERO_BRIGHTNESS_MIN,
  HERO_HEIGHT,
  HERO_WIDTH,
} from '@naya/shared'

import { cropToRectBlob } from '../branding/cropImage.js'

type Stage = 'edit' | 'confirm'

export interface HeroUploadResult {
  /** Cropped 1032×336 PNG with NO brightness applied. */
  blob: Blob
  /** Integer in [HERO_BRIGHTNESS_MIN, HERO_BRIGHTNESS_MAX]. Server applies. */
  brightness: number
}

export function HeroUploadModal({
  file,
  initialOriginalUrl,
  initialBrightness,
  onConfirm,
  onConfirmReEdit,
  onCancel,
  isUploading,
}: {
  /** Source image. Either a fresh File (new upload) or null (re-edit using
   *  `initialOriginalUrl`). */
  file: File | null
  /** Re-edit only: the previously-cropped photo URL (R2). Brightness slider
   *  reopens at `initialBrightness` and the cropper is read-only — re-cropping
   *  requires a fresh upload (and a fresh File). */
  initialOriginalUrl?: string
  /** Re-edit only: brightness to restore the slider to. */
  initialBrightness?: number
  /** Called when the owner confirms a fresh upload. Receives the cropped
   *  PNG Blob + brightness. */
  onConfirm: (result: HeroUploadResult) => void
  /** Called when the owner confirms a brightness-only re-edit (no re-upload).
   *  The caller posts JSON `{brightness}` to the server-side re-edit route —
   *  the browser never has to fetch the R2 original (CORS-blocked). Required
   *  when `initialOriginalUrl` is set. */
  onConfirmReEdit?: (brightness: number) => void
  onCancel: () => void
  isUploading: boolean
}) {
  const isReEdit = !file && !!initialOriginalUrl

  const [stage, setStage] = useState<Stage>('edit')
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [brightness, setBrightness] = useState<number>(
    initialBrightness ?? 0,
  )
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null)
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null,
  )

  // Object URL for the source preview. Fresh File on new uploads; the saved
  // R2 URL on re-edit (no object URL needed, no revoke needed).
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file)
      setImageUrl(url)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setCroppedAreaPixels(null)
      setError(null)
      return () => URL.revokeObjectURL(url)
    } else if (initialOriginalUrl) {
      setImageUrl(initialOriginalUrl)
    }
    return undefined
  }, [file, initialOriginalUrl])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isUploading && !processing) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, isUploading, processing])

  // Pending preview URL lives across stage switches; revoke on unmount.
  useEffect(
    () => () => {
      if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
    },
    [pendingPreviewUrl],
  )

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const busy = isUploading || processing

  async function goToConfirm() {
    setError(null)
    setProcessing(true)
    try {
      if (isReEdit) {
        // Re-edit: no upload needed. Skip blob generation — the server will
        // re-derive from the existing original (CORS-safe, no browser fetch).
        // We still flip to the confirm stage to show the friction modal copy.
        setStage('confirm')
        return
      }
      if (!croppedAreaPixels || !file) {
        throw new Error('Crop your photo before continuing.')
      }
      const blob = await cropToRectBlob(
        file,
        croppedAreaPixels,
        HERO_WIDTH,
        HERO_HEIGHT,
      )
      if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
      setPendingPreviewUrl(URL.createObjectURL(blob))
      setPendingBlob(blob)
      setStage('confirm')
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not process the image. Please try again.',
      )
    } finally {
      setProcessing(false)
    }
  }

  function confirm() {
    if (isReEdit) {
      if (onConfirmReEdit) onConfirmReEdit(brightness)
      return
    }
    if (!pendingBlob) return
    onConfirm({ blob: pendingBlob, brightness })
  }

  return (
    <div
      className="owner-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onCancel()
      }}
    >
      <div
        className="owner-modal owner-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Set hero photo"
        onClick={(e) => e.stopPropagation()}
      >
        {stage === 'edit' ? (
          <>
            <div className="owner-modal-head">
              <h3>{isReEdit ? 'Adjust hero photo' : 'Crop hero photo'}</h3>
            </div>
            <p className="owner-card-sub">
              {isReEdit
                ? "Adjust the brightness so your stamp icons stay visible. Re-cropping requires uploading a new photo."
                : "Drag to reposition and use the slider to zoom. The framed area becomes the background behind your stamp grid (1032×336)."}
            </p>

            {!isReEdit && imageUrl && (
              <div className="owner-crop-stage owner-crop-stage-rect">
                <Cropper
                  image={imageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={HERO_WIDTH / HERO_HEIGHT}
                  cropShape="rect"
                  showGrid
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
            )}
            {isReEdit && imageUrl && (
              <div className="owner-hero-reedit-preview">
                <img
                  src={imageUrl}
                  alt="Saved hero"
                  style={{
                    filter: `brightness(${1 + brightness / 100})`,
                  }}
                />
              </div>
            )}

            {!isReEdit && (
              <label className="owner-crop-zoom">
                <span>Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  disabled={busy}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  aria-label="Zoom"
                />
              </label>
            )}

            <label className="owner-crop-zoom">
              <span>
                Brightness <span aria-hidden="true">({brightness}%)</span>
              </span>
              <input
                type="range"
                min={HERO_BRIGHTNESS_MIN}
                max={HERO_BRIGHTNESS_MAX}
                step={1}
                value={brightness}
                disabled={busy}
                onChange={(e) => setBrightness(Number(e.target.value))}
                aria-label={`Brightness ${brightness}%`}
              />
            </label>
            <p className="owner-field-hint">
              Darken the photo if your stamp icons are hard to see against it.
            </p>

            {error && (
              <p className="owner-field-error" role="alert">
                {error}
              </p>
            )}

            <div className="owner-modal-actions">
              <button
                type="button"
                className="owner-btn"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-primary"
                onClick={() => void goToConfirm()}
                disabled={busy || (!isReEdit && !croppedAreaPixels)}
              >
                {busy ? 'Processing…' : 'Continue'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="owner-modal-head">
              <h3>Confirm pass design</h3>
            </div>
            <p className="owner-card-sub">
              This becomes the background on every customer's pass. New
              customers will see it instantly; existing customers' passes
              update the next time they're stamped.
            </p>

            {(pendingPreviewUrl || initialOriginalUrl) && (
              <div className="owner-hero-confirm-preview">
                <img
                  src={pendingPreviewUrl ?? initialOriginalUrl!}
                  alt="Hero preview"
                  style={{
                    filter: `brightness(${1 + brightness / 100})`,
                  }}
                />
              </div>
            )}

            <div className="owner-modal-actions">
              <button
                type="button"
                className="owner-btn"
                onClick={() => setStage('edit')}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-primary"
                onClick={confirm}
                disabled={busy}
              >
                {busy ? 'Saving…' : 'Save design'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
